import type { CanvasDocumentV3 as CanvasDocument } from "@seedance/contracts";
import type { CanvasHistoryState } from "./model/history.js";

export const canvasToolOrder = ["add", "mode", "batch", "toolbox", "assets", "history", "shortcuts", "help"] as const;
export type CanvasToolId = typeof canvasToolOrder[number];

export interface CanvasToolDefinition {
  id: CanvasToolId;
  label: string;
  ariaLabel: string;
  disabled: boolean;
}

const labels: Record<CanvasToolId, string> = {
  add: "添加",
  mode: "选择和抓手",
  batch: "批量连接",
  toolbox: "我的工具箱",
  assets: "素材库",
  history: "历史素材",
  shortcuts: "快捷键",
  help: "画布帮助"
};

export function canvasTools({ selectedInputCount }: { selectedInputCount: number; canInsertWorkflow: boolean }): CanvasToolDefinition[] {
  return canvasToolOrder.map((id) => ({
    id,
    label: labels[id],
    ariaLabel: labels[id],
    disabled: id === "batch" && selectedInputCount === 0
  }));
}

export interface SessionHistoryEntry { label: string; }

function summary(before: CanvasDocument, after: CanvasDocument): string {
  if (after.nodes.length > before.nodes.length) return "已添加节点";
  if (after.nodes.length < before.nodes.length) return "已删除节点";
  if (after.edges.length !== before.edges.length) return "已修改连线";
  const positions = new Map(before.nodes.map((node) => [node.id, node.position]));
  if (after.nodes.some((node) => {
    const previous = positions.get(node.id);
    return previous !== undefined && (previous.x !== node.position.x || previous.y !== node.position.y);
  })) return "已移动节点";
  return "已编辑画布";
}

export function summarizeCanvasHistory(history: CanvasHistoryState): SessionHistoryEntry[] {
  const snapshots = [...history.past, history.present];
  return snapshots.slice(1).map((document, index) => ({ label: summary(snapshots[index]!, document) })).slice(-8).reverse();
}

export function canvasShortcutRows(): readonly { keys: string; description: string }[] {
  return [
    { keys: "V", description: "框选节点" },
    { keys: "H", description: "移动画布" },
    { keys: "Delete/Backspace", description: "删除所选连线" },
    { keys: "Ctrl/⌘ Z", description: "撤销" },
    { keys: "Ctrl/⌘ Shift+Z 或 Ctrl/⌘ Y", description: "重做" },
    { keys: "Escape", description: "关闭当前菜单" }
  ];
}

export type CanvasHistoryShortcut = "undo" | "redo";

export function canvasHistoryShortcut({
  key,
  ctrlKey = false,
  metaKey = false,
  shiftKey = false,
  editable = false
}: {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  editable?: boolean;
}): CanvasHistoryShortcut | undefined {
  if (editable || (!ctrlKey && !metaKey)) return undefined;
  const normalizedKey = key.toLocaleLowerCase();
  if (normalizedKey === "z") return shiftKey ? "redo" : "undo";
  if (normalizedKey === "y" && !shiftKey) return "redo";
  return undefined;
}
